/**
 * Custom hook for managing item handlers in DrivePanel
 *
 * Extracted from DrivePanel.tsx
 */

import { useCallback } from 'react';
import { apiService, type Document as ApiDocument, type PDFSnippet, type SavedMessage } from '../../../services/api';
import type { SavedContent } from '../../../services/savedContentService';
import type { TableItem } from '../../../services/driveService';
import type { DriveSnippet } from '../types/drive-panel.types';
import logger from '../../../utils/simpleLogger';
import type {
  UseDriveItemHandlersProps,
  UseDriveItemHandlersReturn,
} from '../types/drive-panel.types';

/**
 * Manages handlers for item actions (open, delete, reprocess, etc.)
 */
export function useDriveItemHandlers({
  onDocumentView,
  onSnippetClick,
  onSavedMessageView,
  onStudioResultView,
  onDocumentReprocess,
  updateDocumentStatus,
  loadPrivateDocuments,
  loadSharedDocuments,
  loadSavedData,
}: UseDriveItemHandlersProps): UseDriveItemHandlersReturn {
  // Handle opening items (documents, snippets, messages, studio results)
  const handleItemOpen = useCallback(
    (item: TableItem) => {
      if (item.type === 'document') {
        if (onDocumentView) {
          onDocumentView(item.data as ApiDocument);
        } else {
          console.warn('No onDocumentView handler provided for DrivePanel');
        }
        return;
      }

      if (item.type === 'saved-message') {
        if (onSavedMessageView) {
          onSavedMessageView(item.data as SavedMessage);
        } else {
          console.warn('No onSavedMessageView handler provided for DrivePanel');
        }
        return;
      }

      if (item.type === 'snippet') {
        if (onSnippetClick) {
          onSnippetClick(item.data as DriveSnippet);
        } else {
          console.warn('No onSnippetClick handler provided for DrivePanel');
        }
        return;
      }

      if (item.type === 'studio-result') {
        if (onStudioResultView) {
          onStudioResultView(item.data as SavedContent);
        } else {
          console.warn('No onStudioResultView handler provided for DrivePanel');
        }
      }
    },
    [onDocumentView, onSnippetClick, onSavedMessageView, onStudioResultView]
  );

  // Wrapper for onDocumentReprocess to update status locally immediately
  const handleDocumentReprocess = useCallback(
    (documentId: string) => {
      // Update status locally IMMEDIATELY for instant UI feedback
      updateDocumentStatus(documentId, 'processing');
      // Then call the original handler
      onDocumentReprocess?.(documentId);
    },
    [updateDocumentStatus, onDocumentReprocess]
  );

  const handleSnippetDelete = useCallback(
    async (snippet: PDFSnippet) => {
      if (!confirm('¿Estás seguro de que deseas eliminar este snippet?')) {
        return;
      }

      try {
        await apiService.deletePDFSnippet(snippet.id);
        // Recargar datos guardados
        await loadSavedData();
        logger.info('✅ Snippet eliminado correctamente');
      } catch (error) {
        logger.error('❌ Error eliminando snippet:', error);
        alert('Error al eliminar el snippet. Por favor intenta de nuevo.');
      }
    },
    [loadSavedData]
  );

  const handleSavedMessageDelete = useCallback(
    async (message: SavedMessage) => {
      if (!confirm('¿Estás seguro de que deseas eliminar este mensaje guardado?')) {
        return;
      }

      try {
        await apiService.deleteSavedMessage(message.id);
        // Recargar datos guardados
        await loadSavedData();
        logger.info('✅ Mensaje guardado eliminado correctamente');
      } catch (error) {
        logger.error('❌ Error eliminando mensaje guardado:', error);
        alert('Error al eliminar el mensaje. Por favor intenta de nuevo.');
      }
    },
    [loadSavedData]
  );

  const handleStudioResultDelete = useCallback(
    async (result: SavedContent) => {
      if (!confirm('¿Estás seguro de que deseas eliminar este resultado de studio?')) {
        return;
      }

      try {
        const savedContentService = (await import('../../../services/savedContentService')).default;
        await savedContentService.deleteSavedContent(result.id);
        // Recargar datos guardados
        await loadSavedData();
        logger.info('✅ Resultado de studio eliminado correctamente');
      } catch (error) {
        logger.error('❌ Error eliminando resultado de studio:', error);
        alert('Error al eliminar el resultado. Por favor intenta de nuevo.');
      }
    },
    [loadSavedData]
  );

  const handleRefreshDocuments = useCallback(() => {
    Promise.all([
      loadPrivateDocuments(),
      loadSharedDocuments(),
      loadSavedData(),
    ]);
  }, [loadPrivateDocuments, loadSharedDocuments, loadSavedData]);

  const handleViewConceptualSummary = useCallback(
    (doc: ApiDocument) => {
      const summary = doc.conceptual_summary || null;
      logger.debug('📄 Ver resumen conceptual:', {
        documentId: doc.id,
        documentName: doc.name || doc.original_filename,
        hasSummary: !!summary,
        summaryLength: summary ? summary.length : 0,
        summaryPreview: summary ? summary.substring(0, 100) : null,
      });

      // Return summary data for modal - component will handle setState
      return {
        isOpen: true,
        documentName: doc.name || doc.original_filename || 'Documento',
        conceptualSummary: summary,
      };
    },
    []
  );

  return {
    handleItemOpen,
    handleDocumentReprocess,
    handleSnippetDelete,
    handleSavedMessageDelete,
    handleStudioResultDelete,
    handleRefreshDocuments,
    handleViewConceptualSummary,
  };
}
